import React, { useState, useEffect, useRef } from "react";
import moment from "moment";

import { IDog, IUKCObedienceProgress, IDogRunSummary, ISportProgress } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import { Modal, useModal } from "../../../components/Modal/Modal";
import ManualRunEntryModal from "../ManualRunEntry/ManualRunEntryModal";
import ContextMenu, { IContextMenuOption } from "../../../components/ContextMenu/ContextMenu";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";

import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";

import "./UKCObedienceCard.scss";

interface IProps {
  dog: IDog;
  ukcObedienceData: IUKCObedienceProgress;
  onRunUpdated?: () => void;
}

const UKCObedienceCard: React.FC<IProps> = ({ dog, ukcObedienceData, onRunUpdated }) => {
  const runHistoryModal = useModal();
  const manualRunModal = useModal();
  const deleteConfirmModal = useModal();
  const [editingRun, setEditingRun] = useState<IDogRunSummary | null>(null);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [runToDelete, setRunToDelete] = useState<string | null>(null);
  const [localRuns, setLocalRuns] = useState<IDogRunSummary[]>(ukcObedienceData.recent_runs || []);
  
  // Track if we need to refresh parent when modal closes
  const needsParentRefreshRef = useRef(false);
  
  // Sync localRuns with ukcObedienceData when it changes
  useEffect(() => {
    setLocalRuns(ukcObedienceData.recent_runs || []);
  }, [ukcObedienceData.recent_runs]);

  const handleAddRun = () => {
    setEditingRun(null);
    manualRunModal.toggle();
  };

  const handleEditRun = (run: IDogRunSummary) => {
    setEditingRun(run);
    manualRunModal.toggle();
  };

  const handleRunSaved = async () => {
    manualRunModal.toggle();
    
    // Fetch fresh run data to update the table without closing the modal
    try {
      const response = await apiService.getRibbonRoomData(dog._id);
      const ukcObedienceSport = response.sports.find((s: ISportProgress) => s.show_type === Constants.show_type.ukc_obedience && s.sanctioning_club === Constants.sanctioning_club.UKC);
      if (ukcObedienceSport) {
        setLocalRuns(ukcObedienceSport.recent_runs || []);
      }
    } catch (error) {
      console.error('Failed to refresh runs:', error);
    }
    
    // Mark that we need to refresh parent when the run history modal closes
    needsParentRefreshRef.current = true;
  };

  const handleDeleteRun = (runId: string) => {
    setRunToDelete(runId);
    deleteConfirmModal.toggle();
  };

  // Stable refs for AG Grid cell renderer callbacks (avoids stale closure issues)
  const handleEditRunRef = useRef(handleEditRun);
  const handleDeleteRunRef = useRef(handleDeleteRun);
  const deletingRunIdRef = useRef(deletingRunId);
  useEffect(() => { handleEditRunRef.current = handleEditRun; });
  useEffect(() => { handleDeleteRunRef.current = handleDeleteRun; });
  useEffect(() => { deletingRunIdRef.current = deletingRunId; }, [deletingRunId]);

  const confirmDelete = async () => {
    if (!runToDelete) return;

    setDeletingRunId(runToDelete);
    deleteConfirmModal.toggle();
    
    try {
      await apiService.deleteManualRun(runToDelete);
      toast.show({
        title: "Run Deleted",
        content: "The run has been removed and title progress has been recalculated.",
        duration: 5000,
        type: "success"
      });
      
      // Refresh the local runs list
      const response = await apiService.getRibbonRoomData(dog._id);
      const ukcObedienceSport = response.sports.find((s: ISportProgress) => s.show_type === Constants.show_type.ukc_obedience && s.sanctioning_club === Constants.sanctioning_club.UKC);
      if (ukcObedienceSport) {
        setLocalRuns(ukcObedienceSport.recent_runs || []);
      }
      
      // Mark that parent needs refresh when modal closes
      needsParentRefreshRef.current = true;
    } catch (error: any) {
      toast.show({
        title: "Error",
        content: error.response?.data?.message || "Failed to delete run. Please try again.",
        duration: 5000,
        type: "fail"
      });
    } finally {
      setDeletingRunId(null);
      setRunToDelete(null);
    }
  };

  const handleCloseRunsModal = () => {
    runHistoryModal.toggle();
    
    // If we added/edited/deleted runs, refresh the parent to update title progress
    if (needsParentRefreshRef.current) {
      needsParentRefreshRef.current = false;
      onRunUpdated?.();
    }
  };

  const getActionMenuOptions = (run: IDogRunSummary) => {
    if (!run) return null;
    const isManualEntry = run.data_source === Constants.run_data_source.manual_entry;
    if (run.verified) return null;
    if (deletingRunIdRef.current === run._id) return null;
    if (!isManualEntry) return null;
    return [
      { label: 'Edit', onClick: () => handleEditRunRef.current(run) },
      { label: 'Delete', onClick: () => handleDeleteRunRef.current(run._id) }
    ] as IContextMenuOption[];
  };

  const ActionsCellRenderer = (params: any) => {
    const run = params.data;
    if (!run) return null;
    if (run.verified) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#10b981', fontWeight: 600 }}>Verified</span>;
    if (deletingRunIdRef.current === run._id) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Deleting...</span>;
    const options = params.getOptions(run);
    if (!options) return null;
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><ContextMenu options={options} disablePortal /></div>;
  };

  // AGGrid column definitions
  const columnDefs: ColDef[] = [
    {
      headerName: 'Date',
      field: 'trial_date',
      valueFormatter: (params) => params.value ? moment(params.value).format('M/D/YYYY') : '-',
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Class',
      field: 'class',
      valueFormatter: (params) => params.value || '-',
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Score',
      field: 'score',
      valueFormatter: (params) => params.value !== undefined ? params.value : '-',
      cellStyle: (params) => {
        if (params.value && params.value >= 170) {
          return { color: '#10b981', fontWeight: 600 };
        } else if (params.value && params.value < 170) {
          return { color: '#ef4444', fontWeight: 600 };
        }
        return undefined;
      },
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Placement',
      field: 'placement',
      valueFormatter: (params) => params.value || '-',
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Dogs Defeated',
      field: 'dogs_defeated',
      valueFormatter: (params) => params.value || '-',
      minWidth: 130,
      flex: 1
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: { getOptions: getActionMenuOptions },
      cellStyle: { overflow: 'visible', zIndex: 1 },
      minWidth: 100,
      flex: 1
    }
  ];

  // Sort levels in correct order (Novice -> Open -> Utility)
  const levelOrder = [
    Constants.dog_class_element_level.ukc_obedience_novice,
    Constants.dog_class_element_level.ukc_obedience_open,
    Constants.dog_class_element_level.ukc_obedience_utility
  ];

  const sortedLevels = ukcObedienceData.levels.sort((a, b) => {
    return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
  });

  return (
    <div className="ukcObedienceCard">
      {/* Header */}
      <div className="cardHeader">
        <div className="headerLeft">
          <h3>UKC Obedience</h3>
          <p className="cardSubtitle">Companion & Utility</p>
        </div>
        <div className="headerRight">
          <div className="statsGrid">
            <div className="statItem">
              <div className="statLabel">Total Titles</div>
              <div className="statValue">{ukcObedienceData.earned_titles.length}</div>
            </div>
            <div className="statItem">
              <div className="statLabel">Championship Points</div>
              <div className="statValue">{ukcObedienceData.total_points}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Earned Titles */}
      {ukcObedienceData.earned_titles && ukcObedienceData.earned_titles.length > 0 && (
        <div className="earnedTitlesSection">
          <h4>Earned Titles ({ukcObedienceData.earned_titles.length})</h4>
          <div className="titleBadges">
            {ukcObedienceData.earned_titles.map(title => (
              <div 
                key={title.title_code} 
                className="titleBadge"
                style={{ backgroundColor: title.color_hex || '#4CAF50' }}
              >
                <div className="badgeInfo">
                  <div className="badgeTitle">{title.title_code}</div>
                  <div className="badgeDate">{title.earned_date ? new Date(title.earned_date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress Section - Each level in its own row */}
      <div className="progressSection">
        {/* Basic Levels - Each in own row */}
        {sortedLevels.map((levelData) => {
          // Determine the display name
          const titleCode = levelData.title_code;
          const levelName = levelData.level;
          
          // Extract just the class name from the full level string (e.g., "UKC Obedience Novice" -> "Novice")
          const shortLevelName = levelName.replace('UKC Obedience ', '');
          
          return (
            <div key={levelData.level} className="levelRow">
              <div className="levelHeader">
                <h4>
                  {titleCode ? (
                    <>
                      <strong>{titleCode}</strong> ({shortLevelName})
                    </>
                  ) : (
                    levelName
                  )}
                  {levelData.title_earned && <span className="earnedCheck"> ✓</span>}
                </h4>
              </div>
              <div className="levelStats">
                <div className="statItem">
                  <span className="statLabel">{levelData.qualified_count}/{levelData.required_legs} Legs</span>
                </div>
                {levelData.highest_score && (
                  <div className="statItem">
                    <span className="statLabel">Best: {levelData.highest_score}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Championship Progress - Each in own row */}
        {ukcObedienceData.uoch && (
          <div className="levelRow championshipRow">
            <div className="levelHeader">
              <h4>UOCH {ukcObedienceData.uoch.title_earned && <span className="earnedCheck">✓</span>}</h4>
            </div>
            <div className="levelStats">
              <div className="statItem">
                <span className="statLabel">Points: {ukcObedienceData.uoch.total_points}/{ukcObedienceData.uoch.required_points}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">Awards: {ukcObedienceData.uoch.awards_count}/{ukcObedienceData.uoch.required_awards}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">Judges: {ukcObedienceData.uoch.unique_award_judges}/{ukcObedienceData.uoch.required_award_judges}</span>
              </div>
            </div>
          </div>
        )}

        {ukcObedienceData.ugoch && (
          <div className="levelRow championshipRow">
            <div className="levelHeader">
              <h4>UGOCH {ukcObedienceData.ugoch.title_earned && <span className="earnedCheck">✓</span>}</h4>
            </div>
            <div className="levelStats">
              <div className="statItem">
                <span className="statLabel">Points: {ukcObedienceData.ugoch.total_points}/{ukcObedienceData.ugoch.required_points}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">Awards: {ukcObedienceData.ugoch.awards_count}/{ukcObedienceData.ugoch.required_awards}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">Judges: {ukcObedienceData.ugoch.unique_award_judges}/{ukcObedienceData.ugoch.required_award_judges}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Runs Link */}
      {/* View Run History Link - Always show, even with 0 runs */}
      {onRunUpdated ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={runHistoryModal.toggle}
          >
            View & Edit Run History ({ukcObedienceData.total_runs || 0} runs)
          </button>
        </div>
      ) : ukcObedienceData.recent_runs && ukcObedienceData.recent_runs.length > 0 ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={runHistoryModal.toggle}
          >
            View Run History ({ukcObedienceData.total_runs || 0} runs)
          </button>
        </div>
      ) : null}

      {/* Run History Modal */}
      {runHistoryModal.isShown && (
        <Modal
          isShown={runHistoryModal.isShown}
          hide={handleCloseRunsModal}
          className="runsHistoryModal"
          modalContent={
            <div className="runsModal">
              <h2>UKC Obedience - Run History</h2>

              <div className="runsModalContent">
                <div className="runsSection">
                  <div className="runsSectionHeader">
                    <h4>All Runs</h4>
                  </div>
                  {ukcObedienceData.recent_runs.length === 0 ? (
                    <div className="noRuns">
                      <p>No runs recorded yet.</p>
                    </div>
                  ) : (
                    <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
                      <AgGridReact
                        rowData={localRuns}
                        columnDefs={columnDefs}
                        domLayout='normal'
                        suppressCellFocus={true}
                        theme={themeQuartz}
                        getRowId={(params) => params.data._id}
                      />
                    </div>
                  )}
                </div>

                <div className="modalActions">
                  <Button onClick={handleAddRun}>
                    + Add Manual Run
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      )}

      {/* Manual Run Entry Modal */}
      {manualRunModal.isShown && (
        <ManualRunEntryModal
          isOpen={manualRunModal.isShown}
          onClose={manualRunModal.toggle}
          dog={dog}
          sportType={Constants.show_type.ukc_obedience}
          sanctioningClub={Constants.sanctioning_club.UKC}
          onSuccess={handleRunSaved}
          editRun={editingRun as any}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.isShown && (
        <Modal
          isShown={deleteConfirmModal.isShown}
          hide={deleteConfirmModal.toggle}
          className="deleteConfirmModal"
          modalContent={
            <div className="deleteConfirmContent">
              <h2>Delete Run?</h2>
              <p>Are you sure you want to delete this run? This action cannot be undone.</p>
              <div className="deleteConfirmActions">
                <Button variant="secondary" onClick={deleteConfirmModal.toggle}>
                  Cancel
                </Button>
                <Button variant="default" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default UKCObedienceCard;

